import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Icon } from '@/components/synapse/Icon';
import { useStockIssue } from '@/hooks/useInventory';
import { IssueLineEntry } from '@/features/inventory/issue-lines/IssueLineEntry';
import { IssueLineList } from '@/features/inventory/issue-lines/IssueLineList';
import { humaniseIssueError } from '@/features/inventory/issue-lines/issueErrors';
import { useIssueLines } from '@/features/inventory/issue-lines/useIssueLines';

export function IssuePage() {
  const { t } = useTranslation();
  // The picker itself — choosing an item, its batch and a quantity — is shared
  // with the visit form. This page owns only what is particular to a standalone
  // release: sending the list to the stock ledger and saying so afterwards.
  const lines = useIssueLines();
  const { pending, totals } = lines;
  const [submitted, setSubmitted] = useState<{ count: number; units: number } | null>(null);
  const issue = useStockIssue();

  const onSubmit = async () => {
    if (pending.length === 0) return;
    try {
      await issue.mutateAsync({ entries: lines.entries });
      setSubmitted({ count: totals.count, units: totals.units });
      lines.reset();
    } catch {
      // ApiError captured into issue.error for inline rendering.
    }
  };

  if (submitted) {
    return (
      <SubmitSuccess
        count={submitted.count}
        units={submitted.units}
        onStartAnother={() => setSubmitted(null)}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-col gap-1.5 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">{t('issue.title')}</h1>
        <p className="text-[13.5px] text-muted">{t('issue.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.05fr] lg:items-start lg:gap-6">
        {/* LEFT — entry */}
        <div className="flex flex-col gap-3">
          <Card className="p-0">
            <CardContent className="p-5">
              <IssueLineEntry lines={lines} />

              {issue.isError && (
                <div className="mt-3.5 rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
                  {humaniseIssueError(issue.error, t)}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — running list */}
        <div className="lg:sticky lg:top-4">
          <Card className="overflow-hidden p-0">
            <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
              <div className="flex-1">
                <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('issue.thisRelease')}</div>
                <div className="mt-0.5 text-[12px] text-muted tabular-nums">
                  {totals.count === 0
                    ? t('issue.noLinesYet')
                    : t('issue.linesAndUnits', {
                        count: totals.count,
                        lineLabel: t('common.line', { count: totals.count }),
                        units: totals.units,
                        unitLabel: t('common.unit', { count: totals.units }),
                      })}
                </div>
              </div>
              {pending.length > 0 && (
                <span className="inline-flex h-[22px] items-center rounded-full bg-info-bg px-2 text-[11.5px] font-medium text-info">
                  {t('common.draft')}
                </span>
              )}
            </div>

            <div className="max-h-[540px] overflow-auto px-3.5 py-3.5">
              {pending.length === 0 ? <EmptyList /> : <IssueLineList lines={lines} />}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3.5">
              <div className="text-[12.5px] text-muted">
                {pending.length === 0
                  ? t('issue.addAtLeastOne')
                  : t('issue.submitWillRelease', {
                      count: pending.length,
                      lineLabel: t('common.line', { count: pending.length }),
                    })}
              </div>
              <Button
                type="button"
                onClick={onSubmit}
                disabled={pending.length === 0 || issue.isPending}
              >
                <Icon name="box_out" size={16} />
                {issue.isPending ? t('issue.releasing') : t('issue.releaseBtn')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---------------- subcomponents ----------------

function EmptyList() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="box_out" size={22} />
      </div>
      <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{t('issue.nothingInRelease')}</div>
      <div className="max-w-[260px] text-[12.5px] text-muted">{t('issue.nothingInReleaseBody')}</div>
    </div>
  );
}

interface SubmitSuccessProps {
  count: number;
  units: number;
  onStartAnother: () => void;
}

function SubmitSuccess({ count, units, onStartAnother }: SubmitSuccessProps) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-12 text-center">
      <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-success-bg text-success">
        <Icon name="check" size={36} strokeWidth={2.2} />
      </div>
      <h2 className="mt-5 text-[22px] font-semibold tracking-[-0.02em]">{t('issue.released')}</h2>
      <p className="mt-1 max-w-[280px] text-[13.5px] leading-[1.5] text-muted">
        {t('issue.releasedBody', {
          count,
          lineLabel: t('common.line', { count }),
          units,
          unitLabel: t('common.unit', { count: units }),
        })}
      </p>
      <div className="mt-6 flex w-full flex-col gap-2.5">
        <Button size="lg" full onClick={onStartAnother}>
          <Icon name="plus" size={18} /> {t('issue.startAnotherRelease')}
        </Button>
        <Button asChild size="lg" full variant="ghost">
          <Link to="/">{t('common.backToHome')}</Link>
        </Button>
      </div>
    </div>
  );
}
